/***
 *
 *   WORKFLOWS
 *   A read-only picture of how this account's checks start and run through
 *   Intake, Verify, Screen, Assess and Monitor, and where results are
 *   written back. The server builds the whole view model from live config
 *   (GET /api/account/workflow-view); this view only lays it out.
 *
 *   Open to owners and admins of every account; the endpoint re-checks the
 *   role on every request and answers 403 otherwise.
 *
 **********/

import React, { useCallback, useContext, useEffect, useState } from "react";
import Axios from "axios";
import { Animate, Icon, Loader, Message, ViewContext } from "components/lib";
import { Diagram } from "./diagram";
import { DetailList, SettingsLink, cx } from "./details";
import { StepDrawer } from "./drawer";
import { requestChangeModal } from "./request-change";
import { STAGES, StageIcon } from "./stage-icon";
import styles from "./workflow.module.scss";

const hasOnStep = (trigger, stage) =>
  (trigger?.stages?.[stage] || []).some((step) => step.state === "on");

function formatDate(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

export function WorkflowView() {
  const viewContext = useContext(ViewContext);

  // Loaded here rather than with useAPI, which leaves `loading` set when the
  // request fails. A 403 means the viewer's role may not see the page: it
  // gets its own message and never goes to handleError.
  const [view, setView] = useState({ status: "loading", data: null });
  useEffect(() => {
    let live = true;
    Axios.get("/api/account/workflow-view")
      .then((res) => {
        if (live) setView({ status: "ready", data: res.data.data });
      })
      .catch((err) => {
        if (!live) return;
        if (err?.response?.status === 403) {
          setView({ status: "refused", data: null });
          return;
        }
        setView({ status: "failed", data: null });
        viewContext.handleError(err);
      });
    return () => {
      live = false;
    };
    // load once per visit; viewContext is stable for the view's lifetime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedId, setSelectedId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [drawer, setDrawer] = useState(null);

  const triggers = Array.isArray(view.data?.triggers) ? view.data.triggers : [];
  const selected = triggers.find((t) => t.id === selectedId) || triggers[0] || null;

  const openStep = useCallback((open, element) => setDrawer({ open, returnFocus: element }), []);
  const closeStep = useCallback(() => setDrawer(null), []);

  function toggleExpanded() {
    setDrawer(null);
    setExpanded((value) => !value);
    window.scrollTo({ top: 0 });
  }

  function requestChange() {
    // one at a time: a second request while the modal is open is ignored
    if (viewContext.modal?.data?.show) return;
    viewContext.modal.show(requestChangeModal(expanded ? null : selected?.title));
  }

  if (view.status === "loading") return <Loader />;

  if (view.status === "refused") {
    return (
      <Animate>
        <Message
          type="info"
          title="Workflows is for account owners and admins"
          text="Ask an owner or admin of this account if you need to see how it is set up."
        />
      </Animate>
    );
  }

  if (view.status === "failed" || !view.data) {
    return (
      <Animate>
        <Message
          type="error"
          title="Workflows couldn't load"
          text="We couldn't load how this account is set up. Please reload the page."
        />
      </Animate>
    );
  }

  const { meta = {}, coverage = [], account_panels: panels = [], appendix = [] } = view.data;
  const count = triggers.length;
  const ways = `${count} ${count === 1 ? "way" : "ways"} a check can start`;
  const stageOn = (slug) => {
    const entry = Array.isArray(coverage) ? coverage.find((c) => c.stage === slug) : null;
    return entry ? !!entry.on : triggers.some((t) => hasOnStep(t, slug));
  };
  const shown = expanded ? triggers : [selected].filter(Boolean);

  return (
    <Animate>
      <div className={styles.root}>
        <div className={styles.phead}>
          <div>
            <h1>Your workflows</h1>
            <p>
              How {meta.account_name || "your account"} is set up today: {ways}, each running through
              the five stages. Pick one to see it, or tap any step for the detail.
            </p>
          </div>
          <div className={styles.acts}>
            <span className={styles.ro}>
              <Icon image="lock" size={14} />
              Read-only
            </span>
            <button type="button" className={styles.btn} aria-pressed={expanded} onClick={toggleExpanded}>
              <Icon image={expanded ? "minimize" : "maximize"} size={16} />
              {expanded ? "Collapse" : "Expand all"}
            </button>
            <button type="button" className={cx(styles.btn, styles.pri)} onClick={requestChange}>
              Request a change
            </button>
          </div>
        </div>

        <div className={styles.cover} role="region" aria-label="Your setup">
          <span className={styles.coverLabel} aria-hidden="true">
            Your setup
          </span>
          <ul>
            {STAGES.map((stage) => {
              const on = stageOn(stage.slug);
              return (
                <li key={stage.slug} className={cx(styles.cv, !on && styles.off)} data-wf={stage.slug}>
                  <StageIcon name={stage.slug} />
                  <div>
                    <span className={styles.cvName}>{stage.name}</span>
                    <small>{on ? "On" : "Available to switch on"}</small>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {!expanded && (
          <div className={styles.trigs} role="group" aria-label="How a check starts">
            {triggers.map((trigger) => {
              const included = STAGES.filter((s) => hasOnStep(trigger, s.slug)).map((s) => s.name);
              return (
                <button
                  key={trigger.id}
                  type="button"
                  className={styles.tcard}
                  aria-pressed={trigger.id === selected?.id}
                  onClick={() => setSelectedId(trigger.id)}
                >
                  <span className={styles.tsrc}>
                    <span className={styles.badge}>{trigger.source}</span>
                    {trigger.kind_label && (
                      <span className={cx(styles.kind, trigger.kind === "aml" && styles.aml)}>
                        {trigger.kind_label}
                      </span>
                    )}
                  </span>
                  <span className={styles.tTitle}>{trigger.title}</span>
                  {trigger.short && <span className={styles.tShort}>{trigger.short}</span>}
                  <span className={styles.dots} aria-hidden="true">
                    {STAGES.map((s) => (
                      <i
                        key={s.slug}
                        data-wf={s.slug}
                        className={cx(styles.dot, !hasOnStep(trigger, s.slug) && styles.off)}
                      />
                    ))}
                  </span>
                  <span className={styles.sr}>
                    {included.length ? `Includes ${included.join(", ")}` : "No stages switched on"}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {shown.map((trigger) => (
          <Diagram key={trigger.id} trigger={trigger} expanded={expanded} onOpen={openStep} />
        ))}

        <div className={styles.cta}>
          <div>
            <span className={styles.ctaTitle}>Want it to work differently?</span>
            <p>A StackGo workflow engineer can change triggers, forms, screening and write-backs for you.</p>
          </div>
          <button type="button" className={cx(styles.btn, styles.pri)} onClick={requestChange}>
            Talk to a workflow engineer
          </button>
        </div>

        {Array.isArray(panels) && panels.length > 0 && (
          <section className={styles.every} aria-labelledby="wf-every">
            <div className={styles.sec}>
              <h2 id="wf-every">Applies to every workflow</h2>
              <p>{count > 1 ? `Settings shared by all ${ways}.` : "Settings shared by every check."}</p>
            </div>
            <div className={styles.grid3}>
              {panels.map((panel) => (
                <div key={panel.id || panel.title} className={styles.panel}>
                  <h3>{panel.title}</h3>
                  <SettingsLink settings={panel.settings} />
                  <DetailList rows={panel.rows} />
                </div>
              ))}
            </div>
          </section>
        )}

        {Array.isArray(appendix) && appendix.length > 0 && (
          <details className={styles.all} open={expanded}>
            <summary>Everything in your setup</summary>
            <div className={styles.kv}>
              {appendix.map((section, i) => (
                <div key={`${section.title}-${i}`} className={styles.kvwrap}>
                  <table>
                    <thead>
                      <tr>
                        <th colSpan={2}>{section.title}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(section.rows || []).map((row, j) => (
                        <tr key={j}>
                          <td>{row.label}</td>
                          <td>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </details>
        )}

        <p className={styles.foot}>
          Built from your live configuration on {formatDate(meta.generated_at)}. Read-only.
        </p>
      </div>

      {drawer && (
        <StepDrawer
          key={`${drawer.open.trigger.id}:${drawer.open.stage || "start"}:${drawer.open.step.id || ""}`}
          open={drawer.open}
          returnFocus={drawer.returnFocus}
          onClose={closeStep}
        />
      )}
    </Animate>
  );
}
